import { useId } from 'react'
import { FormattedMessage } from 'react-intl'
import {
  faCircleCheck,
  faDownload,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons'

import { Heading } from 'Components/_theme/Heading/Heading'
import { PrimaryButton } from 'Components/_theme/PrimaryButton/PrimaryButton'
import { Text } from 'Components/_theme/Text/Text'
import { Icon } from 'Components/Icon/Icon'

import { useCopyToClipboard } from 'Portal/src/hooks/useCopyToClipboard/useCopyToClipboard'

import {
  buildDangerousGoodsDownloadUrl,
  buildDownloadUrl,
} from './PublicRequestReturnLabel.api'
import type { PublicRequestReturnLabelSuccessProps } from './PublicRequestReturnLabel.types'

export const PublicRequestReturnLabelSuccess = (
  props: PublicRequestReturnLabelSuccessProps,
) => {
  const { hasDangerousGoods, requestToken, trackingReference } = props
  const trackingNumberId = useId()
  const { copied, copy } = useCopyToClipboard(trackingReference)

  return (
    <div className="PublicRequestReturnLabelSuccess p-6 p-md-9 mx-auto bg-white text-center">
      <div className="PublicRequestReturnLabelSuccess-icon d-flex justify-content-center mb-6">
        <Icon icon={faCircleCheck} size={64} />
      </div>

      <Heading as={2} className="mb-4" size={1}>
        <FormattedMessage id="PublicRequestReturnLabel_success_title" />
      </Heading>

      <Text className="PublicRequestReturnLabelSuccess-description mb-6 text-muted">
        <FormattedMessage id="PublicRequestReturnLabel_success_description" />
      </Text>

      <div className="PublicRequestReturnLabelSuccess-downloadSection PublicRequestReturnLabelSuccess-section p-6 border">
        <a
          className="Button Button-large PrimaryButton w-100 mb-5"
          download
          href={buildDownloadUrl(trackingReference, requestToken)}
          rel="noopener noreferrer"
          target="_blank"
        >
          <span>
            <FormattedMessage id="PublicRequestReturnLabel_success_download" />
          </span>
        </a>

        <div className="text-start">
          <label htmlFor={trackingNumberId}>
            <FormattedMessage id="PublicRequestReturnLabel_success_trackingLabel" />
          </label>
          <div className="d-flex align-items-center gap-3">
            <input
              className="form-control font-monospace flex-grow-1"
              id={trackingNumberId}
              readOnly
              type="text"
              value={trackingReference}
            />
            <PrimaryButton onClick={copy} size="small">
              <FormattedMessage
                id={
                  copied
                    ? 'PublicRequestReturnLabel_success_copied'
                    : 'PublicRequestReturnLabel_success_copy'
                }
              />
            </PrimaryButton>
          </div>
        </div>
      </div>

      {hasDangerousGoods && (
        <div
          aria-live="assertive"
          className="PublicRequestReturnLabelSuccess-dangerousGoodsSection PublicRequestReturnLabelSuccess-section p-6 text-start"
          role="alert"
        >
          <div className="d-flex align-items-center gap-3 mb-3">
            <Icon
              className="PublicRequestReturnLabelSuccess-dangerousGoodsIcon"
              icon={faTriangleExclamation}
              size={20}
            />
            <Heading as={5} className="m-0" size={2}>
              <FormattedMessage id="PublicRequestReturnLabel_success_dangerousGoodsHeading" />
            </Heading>
          </div>
          <p className="PublicRequestReturnLabelSuccess-dangerousGoodsBody mb-6">
            <FormattedMessage id="PublicRequestReturnLabel_success_dangerousGoodsBody" />
          </p>
          <div className="d-flex justify-content-center">
            <a
              className="Button Button-medium Button-icon-left PrimaryButton"
              download
              href={buildDangerousGoodsDownloadUrl(
                trackingReference,
                requestToken,
              )}
              rel="noopener noreferrer"
              target="_blank"
            >
              <Icon icon={faDownload} size={16} />
              <span>
                <FormattedMessage id="PublicRequestReturnLabel_success_dangerousGoodsDownload" />
              </span>
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
